import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import QRCode from 'qrcode';
import { v4 as uuidv4 } from 'uuid';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import HighlightMark from '../../MyComponents/LandingSections/HighlightMark';
import userApi from '../../Api/userApi';
import { ALLOW_WHITEBOARD, HOME_LINK } from '../../common/urlLinks';
import { CARD, TEXT_LINK } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

// Below this the QR panel stacks above the steps.
const STACK = '@media (max-width: 860px)';

const QR_SIZE = 260;
const POLL_MS = 2000;

const useStyles = makeStyles(() => ({
    intro: {
        maxWidth: '760px',
        margin: '0 auto',
        textAlign: 'center',
    },
    eyebrowRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '22px',
    },
    title: {
        margin: '0 0 16px',
        fontSize: 'clamp(2rem, 4.2vw, 3.2rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    lead: {
        maxWidth: '620px',
        margin: '0 auto',
        color: REDESIGN_COLORS.body,
        fontSize: '1.06rem',
        lineHeight: 1.6,
    },
    card: {
        ...CARD,
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gap: 'clamp(24px, 4vw, 56px)',
        alignItems: 'center',
        padding: 'clamp(22px, 3.5vw, 44px)',
        [STACK]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
            justifyItems: 'center',
        },
    },
    // The code sits on its own white tile so it stays scannable on the cream page.
    qrPanel: {
        display: 'grid',
        justifyItems: 'center',
        gap: '14px',
    },
    qrFrame: {
        boxSizing: 'border-box',
        width: `${QR_SIZE + 32}px`,
        height: `${QR_SIZE + 32}px`,
        padding: '16px',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '24px',
        background: REDESIGN_COLORS.white,
        boxShadow: '0 12px 32px rgba(6, 63, 69, 0.09)',
        '& canvas': {
            display: 'block',
            width: `${QR_SIZE}px !important`,
            height: `${QR_SIZE}px !important`,
        },
    },
    qrCaption: {
        margin: 0,
        color: REDESIGN_COLORS.muted,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
        textAlign: 'center',
    },
    steps: {
        display: 'grid',
        gap: '18px',
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    step: {
        display: 'grid',
        gridTemplateColumns: '40px minmax(0, 1fr)',
        gap: '14px',
        alignItems: 'start',
    },
    number: {
        display: 'grid',
        placeItems: 'center',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: REDESIGN_COLORS.primary,
        color: REDESIGN_COLORS.white,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    stepTitle: {
        margin: '8px 0 4px',
        fontSize: '1.05rem',
        lineHeight: 1.3,
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    stepText: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.95rem',
        lineHeight: 1.6,
    },
    // "Waiting for the phone": a pulsing dot on a mist chip.
    waiting: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        marginTop: '22px',
        padding: '10px 16px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.mist,
        color: REDESIGN_COLORS.link,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    pulse: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: REDESIGN_COLORS.primary,
        animation: '$pulse 1.4s ease-in-out infinite',
    },
    '@keyframes pulse': {
        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.35, transform: 'scale(0.7)' },
    },
    backRow: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '26px',
    },
    back: TEXT_LINK,
}));

const STEPS = ['scan', 'signIn', 'done'];

/**
 * Signs this device in from a phone: the page shows a one-time link as a QR
 * code, the visitor scans it and signs in on the phone, and the board reloads
 * signed in once the server reports the link as claimed.
 */
const LinkWhiteboard = () => {
    const classes = useStyles();
    const [link, setLink] = useState(null);

    useEffect(() => {
        setLink(uuidv4());
    }, []);

    useEffect(() => {
        if (!link) return undefined;
        const canvas = document.getElementById('qr-code-whiteboard-link');
        if (canvas) {
            QRCode.toCanvas(canvas, `${window.origin}${ALLOW_WHITEBOARD}/${link}`, { width: QR_SIZE, margin: 1 }, (error) => {
                if (error) console.error(error);
            });
        }
        const interval = setInterval(() => {
            userApi
                .getLinkConnection(link)
                .then(() => {
                    window.location = window.location.origin;
                })
                .catch(() => {});
        }, POLL_MS);
        return () => clearInterval(interval);
    }, [link]);

    return (
        <>
            <LandingSection id="top" maxWidth={1080} paddingTop={46} paddingBottom={16}>
                <div className={classes.intro}>
                    <div className={classes.eyebrowRow}>
                        <SectionEyebrow>
                            <FormattedMessage id="redesign.linkWhiteboard.eyebrow" />
                        </SectionEyebrow>
                    </div>
                    <h1 className={classes.title}>
                        <FormattedMessage
                            id="redesign.linkWhiteboard.title"
                            values={{ mark: (chunks) => <HighlightMark>{chunks}</HighlightMark> }}
                        />
                    </h1>
                    <p className={classes.lead}>
                        <FormattedMessage id="redesign.linkWhiteboard.lead" />
                    </p>
                </div>
            </LandingSection>

            <LandingSection maxWidth={1080} paddingTop={16} paddingBottom={64}>
                <div className={classes.card}>
                    <div className={classes.qrPanel}>
                        <div className={classes.qrFrame}>
                            <canvas id="qr-code-whiteboard-link" width={QR_SIZE} height={QR_SIZE} />
                        </div>
                        <p className={classes.qrCaption}>
                            <FormattedMessage id="redesign.linkWhiteboard.qrCaption" />
                        </p>
                    </div>
                    <div>
                        <ol className={classes.steps}>
                            {STEPS.map((step, index) => (
                                <li key={step} className={classes.step}>
                                    <span aria-hidden="true" className={classes.number}>
                                        {index + 1}
                                    </span>
                                    <div>
                                        <h2 className={classes.stepTitle}>
                                            <FormattedMessage id={`redesign.linkWhiteboard.${step}.title`} />
                                        </h2>
                                        <p className={classes.stepText}>
                                            <FormattedMessage id={`redesign.linkWhiteboard.${step}.text`} />
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                        <div className={classes.waiting} role="status">
                            <span aria-hidden="true" className={classes.pulse} />
                            <FormattedMessage id="redesign.linkWhiteboard.waiting" />
                        </div>
                    </div>
                </div>
                <div className={classes.backRow}>
                    <Link to={HOME_LINK} className={classes.back}>
                        <span aria-hidden="true">←</span>
                        <FormattedMessage id="redesign.linkWhiteboard.back" />
                    </Link>
                </div>
            </LandingSection>
        </>
    );
};

export default LinkWhiteboard;
